import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import Title from '../../component/Title'

const RANKS = Array.from({ length: 10 }, (_, index) => index + 1)

const createEmptySlots = () => RANKS.reduce((slots, rank) => {
    slots[rank] = ''
    return slots
}, {})

const mergeConfiguredProducts = (products, placements) => {
    const productMap = new Map(products.map((product) => [Number(product.productId), product]))

    placements.forEach((placement) => {
        const product = placement.product

        if (product && !productMap.has(Number(product.productId))) {
            productMap.set(Number(product.productId), {
                ...product,
                unavailable: true,
            })
        }
    })

    return [...productMap.values()].sort((first, second) => (
        Number(second.detailType === 'seoul25') - Number(first.detailType === 'seoul25')
    ))
}

const getProductTypeLabel = (product) => product.detailType === 'seoul25'
    ? `서울25 · ${product.packageCode}`
    : '애니멀런'

export default function HomeTop10Promotions() {
    const history = useHistory()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [products, setProducts] = useState([])
    const [slots, setSlots] = useState(createEmptySlots)
    const [snackbar, setSnackbar] = useState({ open: false, severity: 'info', message: '' })

    const loadConfig = useCallback(async () => {
        setLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/homeTop10Promotion/getConfig')

            if (data.code === 403) {
                history.push('/login')
                return
            }

            if (data.code !== 1) {
                throw new Error(data.message || '홈 TOP10 설정을 불러오지 못했습니다.')
            }

            const placements = data.data?.placements || []
            const nextSlots = createEmptySlots()
            placements.forEach((placement) => {
                const rank = Number(placement.displayRank)

                if (RANKS.includes(rank)) {
                    nextSlots[rank] = Number(placement.productId)
                }
            })

            setSlots(nextSlots)
            setProducts(mergeConfiguredProducts(data.data?.products || [], placements))
        } catch (error) {
            setSnackbar({
                open: true,
                severity: 'error',
                message: error.message || '홈 TOP10 설정을 불러오지 못했습니다.',
            })
        } finally {
            setLoading(false)
        }
    }, [history])

    useEffect(() => {
        loadConfig()
    }, [loadConfig])

    const selectedProductIds = useMemo(
        () => new Set(Object.values(slots).filter(Boolean).map(Number)),
        [slots]
    )

    const handleSlotChange = (rank, value) => {
        setSlots((current) => ({
            ...current,
            [rank]: value === '' ? '' : Number(value),
        }))
    }

    const handleSave = async () => {
        setSaving(true)

        try {
            const placements = RANKS
                .filter((rank) => slots[rank])
                .map((displayRank) => ({
                    displayRank,
                    productId: Number(slots[displayRank]),
                }))
            const { data } = await API.post('/api/v1/admin/homeTop10Promotion/save', { placements })

            if (data.code === 403) {
                history.push('/login')
                return
            }

            if (data.code !== 1) {
                throw new Error(data.message || '홈 TOP10 설정을 저장하지 못했습니다.')
            }

            setSnackbar({ open: true, severity: 'success', message: '홈 TOP10 노출 설정을 저장했습니다.' })
            await loadConfig()
        } catch (error) {
            setSnackbar({
                open: true,
                severity: 'error',
                message: error.message || '홈 TOP10 설정을 저장하지 못했습니다.',
            })
        } finally {
            setSaving(false)
        }
    }

    return (
        <Paper sx={{ p: 3, width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Title>홈 TOP10 애니멀런·서울25 노출 관리</Title>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={loadConfig} disabled={loading || saving}>
                        다시 불러오기
                    </Button>
                    <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
                        {saving ? '저장 중...' : '전체 설정 저장'}
                    </Button>
                </Box>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
                지정한 순위에 애니멀런 또는 서울25 상품이 고정 노출되고 기존 국내 마라톤 순위는 뒤로 밀립니다. 빈 순위는 기존 순위 상품으로 채워집니다.
            </Alert>

            {loading ? <Spiner /> : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell width={140}>노출 순위</TableCell>
                            <TableCell>애니멀런·서울25 상품</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {RANKS.map((rank) => (
                            <TableRow key={rank}>
                                <TableCell>
                                    <Typography fontWeight={700}>{rank}위</Typography>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        value={slots[rank]}
                                        onChange={(event) => handleSlotChange(rank, event.target.value)}
                                        disabled={saving}
                                        SelectProps={{ displayEmpty: true }}
                                    >
                                        <MenuItem value="">기존 국내 마라톤 순위 사용</MenuItem>
                                        {products.map((product) => {
                                            const productId = Number(product.productId)
                                            const selectedHere = Number(slots[rank]) === productId
                                            const selectedElsewhere = selectedProductIds.has(productId) && !selectedHere

                                            return (
                                                <MenuItem
                                                    key={productId}
                                                    value={productId}
                                                    disabled={selectedElsewhere || product.unavailable}
                                                >
                                                    [{getProductTypeLabel(product)}] [{productId}] {product.title}{product.unavailable ? ' (현재 노출 불가)' : ''}
                                                </MenuItem>
                                            )
                                        })}
                                    </TextField>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Snackbars
                open={snackbar.open}
                severity={snackbar.severity}
                message={snackbar.message}
                handleClose={() => setSnackbar((current) => ({ ...current, open: false }))}
            />
        </Paper>
    )
}
